import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../components/mobileUi";
import {
  ActionButton,
  ActionPopover,
  ALL_VALUE,
  CenterSpinner,
  DateFilterField,
  EmptyText,
  FilterField,
  FilterPanel,
  ListHeaderRow,
  LoadMore,
  M,
  RowKV,
  SelectSheet,
  StatusChip,
  SummaryCard,
  useSheet,
} from "../../components/managementUi";
import {
  DesktopButton,
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { useEmployeeAuth } from "../../auth/EmployeeAuthContext";
import { EMP_DEPARTMENT, JOB_TITLE, filterJobPosition } from "../../utils/managerRole";
import { ALL_OPTION, buildOptionSheetData, useFilterOptions, validId } from "../../utils/useFilterOptions";
import { approveOutsideApi, getOutsideListApi } from "../../api/registerOutsideManager.api";
import { EMP_BASE_PATH } from "../../config";

const LIMIT = 10;
const fmtDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const STATUS_OPTIONS = [
  { key: ALL_VALUE, label: "Tất cả" },
  { key: "PENDING", label: "Đang chờ duyệt" },
  { key: "APPROVED", label: "Đã duyệt" },
  { key: "REJECTED", label: "Từ chối" },
];
const SORT_OPTIONS = [
  { key: "NEWEST", label: "Mới đến cũ", sortBy: "createdAt", sortOrder: "desc" },
  { key: "OLDEST", label: "Cũ đến mới", sortBy: "createdAt", sortOrder: "asc" },
];

export const outsideStatusUi = (status) => {
  if (status === "APPROVED") return { label: "ĐÃ DUYỆT", bg: "#E9FFF4", color: "#0BA859" };
  if (status === "REJECTED") return { label: "TỪ CHỐI", bg: "#FFF1F0", color: "#E5484D" };
  return { label: "ĐANG CHỜ DUYỆT", bg: "#FFF7E8", color: "#F79D0D" };
};

const OutsideCard = ({ item, onClick, onOpenMenu }) => {
  const ui = outsideStatusUi(item?.status);
  const emp = item?.employee || item?.employeeObject || {};
  return (
    <Box
      role="button"
      tabIndex={0}
      w="100%"
      textAlign="left"
      cursor="pointer"
      onClick={onClick}
      bg="white"
      border="1px solid"
      borderColor={M.border}
      borderRadius="12px"
      p="14px"
    >
      <Flex w="100%" align="center" justify="space-between" gap="8px">
        <Box minW="0">
          <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
            {item?.employeeObject?.fullName || emp?.fullName || "-"} - {item?.employeeObject?.code || emp?.code || "-"}
          </Text>
          <Box h="8px" />
          <Flex align="center" gap="8px" flexWrap="wrap">
            <Text fontSize="14px" fontWeight={500} color={M.baseContent}>
              {fmtDate(item?.createdAt)}
            </Text>
            <StatusChip label={ui.label} bg={ui.bg} color={ui.color} />
          </Flex>
        </Box>
        {item?.status === "PENDING" ? <ActionButton onOpen={(rect) => onOpenMenu(item, rect)} /> : null}
      </Flex>

      <Box h="1px" bg={M.border} mt="12px" mb="12px" />

      <Flex direction="column" gap="8px">
        <RowKV label="Địa điểm" value={item?.checkinPlace || "-"} />
        <RowKV label="Số công" value={`${item?.workingDayRate || 0}`} />
        <RowKV label="Lý do" value={item?.comment || "-"} />
        {emp?.department === EMP_DEPARTMENT.SUPPORT_BUSINESS && emp?.supportDepartment?.name && (
          <RowKV label="Phòng ban" value={emp?.supportDepartment?.name} />
        )}
        {emp?.department === EMP_DEPARTMENT.BUSINESS && emp?.saleBlock?.name && (
          <RowKV label="Khối" value={emp?.saleBlock?.name} />
        )}
        {emp?.department === EMP_DEPARTMENT.BUSINESS && emp?.saleArea?.name && (
          <RowKV label="Vùng" value={emp?.saleArea?.name} />
        )}
        <RowKV label="Chi nhánh" value={emp?.branch?.name || "-"} />
      </Flex>
    </Box>
  );
};

const OutsideApprovalList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const toast = useToast();
  const { user } = useEmployeeAuth();
  const jobPosition = filterJobPosition(user);

  const [filterOpen, setFilterOpen] = useState(true);
  const [status, setStatus] = useState("PENDING");
  const [area, setArea] = useState(ALL_OPTION);
  const [block, setBlock] = useState(ALL_OPTION);
  const [supportDept, setSupportDept] = useState(ALL_OPTION);
  const [fromDate, setFromDate] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [sortKey, setSortKey] = useState("NEWEST");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  const [menu, setMenu] = useState(null);

  const sheet = useSheet();
  const [sheetSearch, setSheetSearch] = useState("");
  const sheetExtra = useMemo(
    () => (sheet.type === "BLOCK" ? { saleAreaId: validId(area) } : undefined),
    [sheet.type, area]
  );
  const options = useFilterOptions(sheet.type, sheetSearch, sheetExtra);

  const sortOption = SORT_OPTIONS.find((s) => s.key === sortKey) || SORT_OPTIONS[0];

  const buildParams = useCallback(
    (p) => ({
      page: p,
      limit: LIMIT,
      fromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : undefined,
      toDate: toDate ? moment(toDate).format("YYYY-MM-DD") : undefined,
      status: status !== ALL_VALUE ? status : undefined,
      sortBy: sortOption.sortBy,
      sortOrder: sortOption.sortOrder,
      saleAreaId: validId(area),
      saleBlockId: validId(block),
      supportDepartmentId: validId(supportDept),
    }),
    [fromDate, toDate, status, sortOption, area, block, supportDept]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getOutsideListApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setTotal(data?.pagination?.total ?? list.length);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, fromDate, toDate, sortKey, area?._id, block?._id, supportDept?._id]);

  const approve = useCallback(
    async (item, nextStatus) => {
      try {
        await approveOutsideApi(item._id, nextStatus);
        toast({ title: "Duyệt Thành công", status: "success", duration: 2500, position: "top" });
        fetchPage(1, "replace");
      } catch (e) {
        toast({ title: e?.message || "Duyệt thất bại", status: "error", duration: 3000, position: "top" });
      }
    },
    [toast, fetchPage]
  );

  const statusLabel = STATUS_OPTIONS.find((s) => s.key === status)?.label ?? "Tất cả";

  const filterFields = useMemo(() => {
    const f = {
      status: <FilterField key="status" label="Trạng thái" value={statusLabel} onClick={() => sheet.open("STATUS")} />,
      area: <FilterField key="area" label="Vùng" value={area?.name || "Tất cả"} onClick={() => sheet.open("AREA")} />,
      block: <FilterField key="block" label="Khối" value={block?.name || "Tất cả"} onClick={() => sheet.open("BLOCK")} />,
      sdept: (
        <FilterField
          key="sdept"
          label="Phòng ban"
          value={supportDept?.name || "Tất cả"}
          onClick={() => sheet.open("SUPPORT_DEPARTMENT")}
        />
      ),
      from: <DateFilterField key="from" label="Từ ngày" value={fromDate} onChange={setFromDate} />,
      to: <DateFilterField key="to" label="Đến ngày" value={toDate} onChange={setToDate} />,
    };
    switch (jobPosition) {
      case JOB_TITLE.TRUONG_KHOI:
        return [f.status, f.from, f.to];
      case JOB_TITLE.TRUONG_VUNG:
        return [f.status, f.block, f.from, f.to];
      case JOB_TITLE.TRUONG_PHONG:
        return [f.status, f.sdept, f.from, f.to];
      default:
        return [f.status, f.area, f.block, f.from, f.to];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPosition, statusLabel, area, block, supportDept, fromDate, toDate]);

  const sheetTitle =
    { STATUS: "Trạng thái", SORT: "Sắp xếp", AREA: "Vùng", BLOCK: "Khối", SUPPORT_DEPARTMENT: "Phòng ban" }[sheet.type] || "";

  const sheetData = useMemo(() => {
    if (sheet.type === "STATUS") return STATUS_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SORT") return SORT_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "AREA") return buildOptionSheetData(options.items, area);
    if (sheet.type === "BLOCK") return buildOptionSheetData(options.items, block);
    if (sheet.type === "SUPPORT_DEPARTMENT") return buildOptionSheetData(options.items, supportDept);
    return [];
  }, [sheet.type, options.items, area, block, supportDept]);

  const selectedKey = {
    STATUS: status,
    SORT: sortKey,
    AREA: area?._id,
    BLOCK: block?._id,
    SUPPORT_DEPARTMENT: supportDept?._id,
  }[sheet.type];

  const onPick = (raw) => {
    if (sheet.type === "STATUS") setStatus(raw);
    if (sheet.type === "SORT") setSortKey(raw);
    if (sheet.type === "AREA") setArea(raw);
    if (sheet.type === "BLOCK") setBlock(raw);
    if (sheet.type === "SUPPORT_DEPARTMENT") setSupportDept(raw);
    sheet.close();
    setSheetSearch("");
  };

  const popovers = (
    <>
      <ActionPopover
        open={!!menu}
        anchor={menu?.rect}
        onClose={() => setMenu(null)}
        items={[
          {
            key: "approve",
            label: "Phê duyệt",
            icon: appImages.icon_check_circle,
            onPress: () => approve(menu.item, "APPROVED"),
          },
          {
            key: "reject",
            label: "Từ chối",
            icon: appImages.icon_reject_circle,
            danger: true,
            onPress: () => approve(menu.item, "REJECTED"),
          },
        ]}
      />

      <SelectSheet
        isOpen={sheet.isOpen}
        onClose={() => {
          sheet.close();
          setSheetSearch("");
        }}
        title={sheetTitle}
        data={sheetData}
        selectedKey={selectedKey}
        onSelect={onPick}
        searchable={!["STATUS", "SORT"].includes(sheet.type)}
        searchValue={sheetSearch}
        onChangeSearch={setSheetSearch}
        loading={options.loading}
        loadingMore={options.loadingMore}
        hasNextPage={options.hasMore}
        onEndReached={options.loadMore}
      />
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it) => {
      const emp = it?.employeeObject || it?.employee;
      const ui = outsideStatusUi(it?.status);
      return {
        id: it._id,
        raw: it,
        ui,
        employee: `${it?.employeeObject?.fullName || emp?.fullName || "-"} - ${
          it?.employeeObject?.code || emp?.code || "-"
        }`,
        unit:
          emp?.department === EMP_DEPARTMENT.SUPPORT_BUSINESS
            ? emp?.supportDepartment?.name || "-"
            : [emp?.saleBlock?.name, emp?.saleArea?.name].filter(Boolean).join(" / ") || "-",
        branch: emp?.branch?.name || "-",
        createdAt: fmtDate(it?.createdAt),
        place: it?.checkinPlace || "-",
        rate: `${it?.workingDayRate || 0}`,
        comment: it?.comment || "-",
      };
    });

    return (
      <DesktopPage title="Phê duyệt chấm công ngoài">
        <DesktopStatRow items={[{ key: "total", title: "Tổng số đơn", total: total || 0 }]} />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            {filterFields.map((el, i) =>
              React.cloneElement(el, { key: el.key || i, width: "220px" }),
            )}
            <Box w="220px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                border="1px solid"
                borderColor={M.border}
                borderRadius="8px"
                bg="white"
                fontSize="13px"
                onClick={() => sheet.open("SORT")}
              >
                {sortOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có đơn chấm công ngoài"
            onRowClick={(row) =>
              navigate(`${EMP_BASE_PATH}/quan-ly/phe-duyet-cham-cong-ngoai/${row.id}`)
            }
            rows={rows}
            headers={[
              { key: "employee", label: "Nhân viên", align: "left", width: "18%" },
              { key: "unit", label: "Khối / Vùng / Phòng", align: "left", width: "14%" },
              { key: "branch", label: "Chi nhánh", align: "left", width: "12%" },
              { key: "createdAt", label: "Ngày tạo", width: "10%" },
              { key: "place", label: "Địa điểm", align: "left", width: "14%" },
              { key: "rate", label: "Số công", width: "7%" },
              { key: "comment", label: "Lý do", align: "left", width: "12%" },
              {
                key: "status",
                label: "Trạng thái",
                width: "9%",
                render: (row) => (
                  <StatusChip label={row.ui.label} bg={row.ui.bg} color={row.ui.color} />
                ),
              },
              {
                key: "actions",
                label: "Thao tác",
                width: "10%",
                render: (row) =>
                  row.raw.status === "PENDING" ? (
                    <Flex gap="8px" justify="center" onClick={(e) => e.stopPropagation()}>
                      <DesktopButton
                        h="32px"
                        p="0 10px"
                        fontSize="13px"
                        onClick={() => approve(row.raw, "APPROVED")}
                      >
                        Duyệt
                      </DesktopButton>
                      <DesktopButton
                        variant="danger"
                        h="32px"
                        p="0 10px"
                        fontSize="13px"
                        onClick={() => approve(row.raw, "REJECTED")}
                      >
                        Từ chối
                      </DesktopButton>
                    </Flex>
                  ) : (
                    "-"
                  ),
              },
            ]}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {popovers}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Phê duyệt chấm công ngoài" />

      <Box px="16px" pt="12px">
        <FilterPanel open={filterOpen} onToggle={() => setFilterOpen((p) => !p)}>
          {filterFields}
        </FilterPanel>

        <Box h="12px" />
        <SummaryCard text="TỔNG SỐ LOCKING" total={total} />

        <ListHeaderRow
          title="Danh sách đơn chấm công ngoài"
          sortLabel={sortOption.label}
          onSort={() => sheet.open("SORT")}
        />

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText text="Không có đơn chấm công ngoài" />
        ) : (
          <Flex direction="column" gap="12px">
            {items.map((it) => (
              <OutsideCard
                key={it._id}
                item={it}
                onClick={() => navigate(`${EMP_BASE_PATH}/quan-ly/phe-duyet-cham-cong-ngoai/${it._id}`)}
                onOpenMenu={(item, rect) => setMenu({ item, rect })}
              />
            ))}
            <LoadMore hasNext={hasNext} loading={loading} onClick={() => fetchPage(page + 1, "append")} />
          </Flex>
        )}
      </Box>

      {popovers}
    </Box>
  );
};

export default OutsideApprovalList;
